import { changeSplash } from '@/actions/splashActions';
import {
    stateConfig,
    stateLastVisitedNotifications,
    stateNotifications,
    stateSplash,
} from '@/selectors/selectors';
import { SplashState } from '@/state/SplashState';
import { useDispatch, useSelector } from '@/state/store';
import { forwardRef, useState, useEffect, FC } from 'react';
import { Config } from '@/bootstrap/config';
import {
    Feilmelding,
    fetchFeilmeldinger,
} from '@/components/notification/NotificationDialog';
import { LAST_SEEN_STATUS_ALERT_STORAGE_KEY } from '@/state/MetaState';

const NotificationButton = forwardRef<HTMLButtonElement>((_, ref) => {
    const dispatch = useDispatch();
    const config: Config = useSelector(stateConfig);
    const splashState = useSelector(stateSplash);
    const lastVisitedState = useSelector(stateLastVisitedNotifications);
    const notificationsState = useSelector(stateNotifications).toSorted(
        (a, b) => b.time.getTime() - a.time.getTime()
    );

    const [statusAlert, setStatusAlert] = useState<Feilmelding>();

    useEffect(() => {
        fetchFeilmeldinger(config).then(res => {
            setStatusAlert(
                res
                    .filter(f => f.arsak !== 'INGEN_PROBLEMER')
                    .sort(
                        (a, b) =>
                            new Date(b.datoLaget).getTime() -
                            new Date(a.datoLaget).getTime()
                    )[0]
            );
        });
    }, [config]);

    const iconColor = splashState === SplashState.NOTIFICATION ? 'orange' : 'inherit';
    const showNotificationMessage = splashState !== SplashState.CHANGELOG;

    const hasNotification = () => {
        const alertUnread = statusAlert
            ? new Date(statusAlert.datoLaget).getTime() > lastVisitedState.getTime()
            : false;
        const notifUnread = notificationsState.some(
            n => n.time.getTime() > lastVisitedState.getTime()
        );
        return alertUnread || notifUnread;
    };

    const [unseenAlertMessage, setUnseenAlertMessage] = useState<string | undefined>(
        undefined
    );

    useEffect(() => {
        if (statusAlert) {
            const lastSeen = localStorage.getItem(LAST_SEEN_STATUS_ALERT_STORAGE_KEY);
            if (lastSeen !== statusAlert.datoLaget) {
                setUnseenAlertMessage(statusAlert.tittel);
            }
            localStorage.setItem(
                LAST_SEEN_STATUS_ALERT_STORAGE_KEY,
                statusAlert.datoLaget
            );
        }
    }, [statusAlert]);
    return (
        <button
            ref={ref}
            type="button"
            aria-controls="landingpage"
            aria-owns="landingpage"
            aria-label="Varsler"
            onClick={() => dispatch(changeSplash(SplashState.NOTIFICATION))}
            className="c-splash-button o-btn-square"
            title="Varsler"
            style={{ position: 'relative' }}
        >
            <i
                className="material-icons"
                aria-hidden="true"
                style={{ color: iconColor, position: 'relative' }}
            >
                notifications
            </i>
            {hasNotification() && (
                <i className="material-icons c-notifications-circle">circle</i>
            )}
            {showNotificationMessage && (
                <NotificationMessage message={unseenAlertMessage} />
            )}
        </button>
    );
});

NotificationButton.displayName = 'NotificationButton';
export { NotificationButton };

interface NotificationMessageProps {
    message?: string;
}

const NotificationMessage: FC<NotificationMessageProps> = ({ message }) => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!message || message.trim() === '') return;
        setShow(true);
        const times = setTimeout(() => setShow(false), 5000);

        return () => {
            clearTimeout(times);
        };
    }, [message]);

    if (!message || message.trim() === '') return null;

    return (
        <span
            onClick={e => e.stopPropagation()}
            style={{
                position: 'absolute',
                left: '100%',
                top: '50%',
                transform: 'translateY(-50%)',
                marginLeft: '.5rem',
                cursor: 'default',
            }}
        >
            <span
                style={{
                    maxWidth: show ? '100%' : '0',
                    opacity: show ? '100%' : '0',
                    display: 'flex',
                    float: 'left',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    transition: 'all 1s ease-in-out',
                }}
            >
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <span
                        aria-hidden
                        style={{
                            backgroundColor: 'lightyellow',
                            width: '1.2rem',
                            height: '1.2rem',
                            marginLeft: '.25rem',
                            marginRight: '-0.9rem',
                            transform: 'rotate(45deg)',
                            zIndex: '1',
                        }}
                    />
                    <label
                        style={{
                            padding: '.25rem 1rem',
                            borderRadius: '2rem',
                            backgroundColor: 'lightyellow',
                            textWrap: 'nowrap',
                        }}
                    >
                        {message}
                    </label>
                </span>
            </span>
        </span>
    );
};
